import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGoBack } from "@/lib/useGoBack";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import PhotoUpload from "@/components/PhotoUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import MapLink from "@/components/MapLink";
import { programAudienceParts } from "@/lib/sports";
import ShareButton from "@/components/ShareButton";
import { BookmarkPlus, BookmarkCheck, MapPin, Mail, Phone, Globe, Trophy, Users, ExternalLink, BadgeCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import TeamLogo from "@/components/TeamLogo";
import Reviews from "@/components/Reviews";
import SocialsDisplay from "@/components/SocialsDisplay";
import CreatorTag from "@/components/CreatorTag";
import { circuitSlug } from "@/lib/seo";
import { sportToSlug } from "@/lib/sportRoutes";
import ClaimedByTag from "@/components/ClaimedByTag";
import CircuitFlag from "@/components/CircuitFlag";
import EditHistory from "@/components/EditHistory";
import WikiEditModal from "@/components/WikiEditModal";
import MessageBoard from "@/components/MessageBoard";
import CollaboratorsManager from "@/components/CollaboratorsManager";
import { ManageModeratorsModal } from "@/components/ClaimedListingsPanel";
import FlagButton from "@/components/FlagButton";
import EntityEvents from "@/components/EntityEvents";
import DistanceZip from "@/components/DistanceZip";
import { savedNearZip, getSavedFilters, saveFilters } from "@/lib/filterCookies";
import { baseProgramName } from "@/lib/programs";
import { sortProgramRosters, hasCoach, teamIdentifier } from "@/lib/teamSort";
import { InlineAddTeamForm } from "@/pages/Account";
import { AddTeamForm } from "@/pages/Add";
import ContactsDisplay from "@/components/ContactsDisplay";
import useFitOneLine from "@/lib/useFitOneLine";
import HeroLogoBadge from "@/components/HeroLogoBadge";
import { usePhotoLightbox, HeroCarousel } from "@/components/PhotoGallery";
import FollowAlertsBell from "@/components/FollowAlertsBell";
import ConfirmDialog from "@/components/ConfirmDialog";
import Seo from "@/components/Seo";

// A program's rosters (13U, 14U…) are separate rows with their own collision slugs
// (tennesseeimpact, -2, -3…) but ALL render the same program page. Pick ONE
// representative slug so every roster URL canonicalizes to a single indexable page.
// Scoped to same-program siblings (the /program endpoint already groups by
// base-name + city + state + sport), so two different-city teams that happen to
// share a name never collapse into each other.
function programRepSlug(team, rosters) {
    const slugs = (rosters || []).map((r) => r && r.slug).filter(Boolean);
    const pool = slugs.length ? slugs : [team && (team.slug || team.id)].filter(Boolean);
    const noSuffix = pool.filter((s) => !/-\d+$/.test(s));
    const chosen = (noSuffix.length ? noSuffix : pool).slice().sort()[0];
    return chosen || (team && (team.slug || team.id));
}

export default function TeamDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const goBack = useGoBack();
    const { user, isFollowing, toggleFollow } = useAuth();
    const [team, setTeam] = useState(null);
    const [rosters, setRosters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [claimOpen, setClaimOpen] = useState(false);
    const [claimMsg, setClaimMsg] = useState("");
    const [claimLink, setClaimLink] = useState("");
    const [claimImage, setClaimImage] = useState("");
    const [showMods, setShowMods] = useState(false);
    const [showRelinquish, setShowRelinquish] = useState(false);
    const [pubEdit, setPubEdit] = useState(false);
    const [eventsBump, setEventsBump] = useState(0);
    const [boysFirst, setBoysFirst] = useState(true);
    const [claiming, setClaiming] = useState(false);
    const [followBusy, setFollowBusy] = useState(false);
    const [editingProgram, setEditingProgram] = useState(false);
    const [ownerEditOpen, setOwnerEditOpen] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [zipState, setZipState] = useState(savedNearZip());
    const [coords, setCoords] = useState(null);  // precise device lat/lng, when granted
    const { open: openPhoto, node: photoLightbox } = usePhotoLightbox((team && team.photos) || [], (team && team.name) || "");
    const nameFitRef = useFitOneLine(team ? (baseProgramName(team) || team.name || "") : "", { reserve: team && team.owner_verified ? 40 : 0 });
    const handleZip = (z, c) => {
        setZipState(z);
        setCoords(c || null);
        saveFilters({ ...getSavedFilters({}), near_zip: z });
    };

    useEffect(() => {
        setLoading(true);
        const params = {};
        if (coords) { params.near_lat = coords.lat; params.near_lng = coords.lng; if (zipState) params.near_zip = zipState; }
        else if (zipState) { params.near_zip = zipState; }
        Promise.all([
            api.get(`/teams/${id}`, { params }).then((r) => r.data).catch(() => null),
            api.get(`/teams/${id}/program`).then((r) => r.data).catch(() => []),
        ]).then(([t, siblings]) => {
            setTeam(t);
            setPubEdit(!!(t && t.allow_public_edit));
            setRosters(Array.isArray(siblings) && siblings.length ? siblings : (t ? [t] : []));
        }).finally(() => setLoading(false));
    }, [id, reloadKey, zipState, coords]);

    // Canonicalize the URL to the program's representative SEO slug. Old UUID links
    // AND non-representative roster slugs (tennesseeimpact-3 …) both redirect to the
    // single program page (tennesseeimpact), so Google indexes ONE page per program.
    useEffect(() => {
        if (!team) return;
        const rep = programRepSlug(team, rosters);
        if (rep && id !== rep) navigate(`/teams/${rep}`, { replace: true });
    }, [team, rosters, id, navigate]);

    if (loading && !team) return <div className="w-full px-4 sm:px-6 lg:px-10 py-20 text-center text-[#a0a0a0]">Loading…</div>;
    if (!team) return (
        <div className="w-full px-4 sm:px-6 lg:px-10 py-20 text-center">
            <div className="font-display text-4xl">Team not found</div>
            <button onClick={goBack} data-testid="not-found-back" className="text-[#ff4500] text-sm uppercase tracking-wider mt-4 inline-block">← Back</button>
        </div>
    );

    const isFollowingTeam = isFollowing("team", team.id);
    const isOwner = user && team.owner_user_id === user.id;
    // Owner-only management controls (collaborators, review responses) should only
    // appear when the user is actually acting as the team director — not when the
    // same account is browsing under their fan profile.
    const isManaging = !!(user && (user.is_admin || (isOwner && user.active_profile === "team_director")));
    const isClaimed = !!team.owner_user_id;
    const isVerified = team.owner_verified;
    const programName = baseProgramName(team) || team.name;
    const canonicalSlug = programRepSlug(team, rosters);
    // Age groups across the whole program (e.g. "13U–17U") — differentiates the one
    // indexed program page and gives it richer, unique SEO content.
    const programAges = Array.from(new Set((rosters.length ? rosters : [team]).map((r) => r && r.age_group).filter(Boolean)))
        .sort((a, b) => (parseInt(String(a).replace(/\D/g, ""), 10) || 0) - (parseInt(String(b).replace(/\D/g, ""), 10) || 0));
    const ageLabel = programAges.length
        ? (programAges.length === 1 ? programAges[0] : `${programAges[0]}–${programAges[programAges.length - 1]}`)
        : "";
    // Layout format (formerly AEBL-only) is now applied to all team pages.
    const isAebl = true;
    const hasHeaderAvatar = !!(team.avatar || team.logo);
    // The -ml-24 offset only makes sense when the side avatar column is actually
    // rendered (line ~304). With a hero photo there's no side avatar, so skip it
    // or the meta content gets pulled off the left edge and clipped.
    const showSideAvatar = !team.photo && hasHeaderAvatar;
    // Build a compact roster label — "Boys 2029", "Girls 14U", "Boys 8th" — matching the
    // multi-select used when the team was created (gender then identifier).
    const rosterLabel = (r) => {
        const g = r.gender === "F" ? "Girls" : "Boys";
        const id = r.age_group || r.grade || r.grad_year || "";
        return id ? `${g} ${id}` : g;
    };

    // Sort program rosters: boys first, then grade teams (high→low grade)
    // before age teams (high→low age), then grad-year teams.
    const GRADE_HIGH_TO_LOW = ["12th", "11th", "10th", "9th", "8th", "7th", "6th", "5th", "4th", "3rd", "2nd", "1st"];
    const rosterSortTuple = (r) => {
        const g = r.gender === "F" ? 1 : 0;
        if (r.grade) return [g, 0, GRADE_HIGH_TO_LOW.indexOf(r.grade)];
        if (r.age_group) return [g, 1, -(parseInt(r.age_group, 10) || 0)];
        if (r.grad_year) return [g, 2, parseInt(r.grad_year, 10) || 0];
        return [g, 3, 0];
    };
    const sortedProgramRosters = [...rosters].sort((a, b) => {
        const ta = rosterSortTuple(a), tb = rosterSortTuple(b);
        for (let i = 0; i < ta.length; i++) if (ta[i] !== tb[i]) return ta[i] - tb[i];
        return 0;
    });
    // Head coach + contacts for a roster (prefers team_coaches entry, falls back to legacy fields).
    const rosterHeadCoach = (r) => {
        const tc = Array.isArray(r.team_coaches) && r.team_coaches.length ? r.team_coaches[0] : null;
        const name = (tc && tc.name) || r.coach || "";
        let contacts = (tc && Array.isArray(tc.contacts) && tc.contacts.length) ? tc.contacts : [];
        if (!contacts.length) {
            contacts = [
                r.contact_email ? { type: "email", value: r.contact_email } : null,
                r.contact_phone ? { type: "phone", value: r.contact_phone } : null,
            ].filter(Boolean);
        }
        return { name, contacts };
    };

    const onFollow = async () => {
        if (!user) return toast.error("Please log in to follow");
        setFollowBusy(true);
        const res = await toggleFollow("team", team.id);
        setFollowBusy(false);
        if (!res.ok) return toast.error(res.error || "Could not update follow");
        toast.success(res.following ? "You'll be notified when this team posts new listings" : "Unfollowed");
    };

    const isCreator = !!(user && team.created_by === user.id);
    const withinDeleteWindow = (() => {
        if (!team.created_at) return false;
        const dt = new Date(team.created_at);
        if (isNaN(dt)) return false;
        return (Date.now() - dt.getTime()) <= 7 * 24 * 60 * 60 * 1000;
    })();
    const deleteRemaining = (() => {
        if (!team.created_at) return "";
        const end = new Date(team.created_at).getTime() + 7 * 24 * 60 * 60 * 1000;
        const ms = end - Date.now();
        if (ms <= 0) return "";
        const d = Math.floor(ms / 86400000);
        const h = Math.floor((ms % 86400000) / 3600000);
        return d > 0 ? `${d}d ${h}h left` : `${h}h left`;
    })();
    const canCreatorDelete = isCreator && withinDeleteWindow;
    const canDelete = !!(user?.is_admin || canCreatorDelete);

    const onRelinquish = async () => {
        try {
            await api.post(`/teams/${team.id}/relinquish`);
            toast.success("You've relinquished ownership");
            const fresh = await api.get(`/teams/${team.id}`);
            setTeam(fresh.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not relinquish");
        }
    };

    const onTogglePublicEdit = async () => {
        const val = !pubEdit;
        try {
            await api.patch(`/teams/${team.id}/program`, { allow_public_edit: val });
            setPubEdit(val);
            toast.success(val ? "Public edits enabled — anyone signed in can improve this listing" : "Public edits disabled");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not update public edits");
        }
    };

    const onDelete = async () => {
        if (!window.confirm("Delete this listing? Its reviews and followers go too — you'll get a few seconds to undo.")) return;
        try {
            const { data } = await api.delete(`/teams/${team.id}`);
            if (data?.bin_id) {
                toast.success("Listing deleted", {
                    duration: 9000,
                    action: {
                        label: "Undo",
                        onClick: async () => {
                            try { await api.post(`/teams/restore/${data.bin_id}`); toast.success("Restored"); navigate(`/teams/${team.id}`); }
                            catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Undo failed"); }
                        },
                    },
                });
            } else {
                toast.success("Listing deleted");
            }
            navigate("/");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not delete");
        }
    };

    const onClaim = async () => {
        if (!user) {
            toast.error("Please log in to claim this listing");
            return;
        }
        if (!user.is_pro) {
            toast.error("Only Pro members can claim listings — upgrade to Pro on your Account page");
            return;
        }
        setClaiming(true);
        try {
            await api.post(`/claims/team/${team.id}`);
            toast.success("Listing claimed — your name and contact matched");
            const fresh = await api.get(`/teams/${team.id}`);
            setTeam(fresh.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not claim this listing");
        } finally {
            setClaiming(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-8" data-testid="team-detail-page">
            <Seo
                title={`${programName}${team.sport ? ` — ${team.sport} Travel Program` : ""}${ageLabel ? ` ${ageLabel}` : ""}${team.city && team.state ? ` (${team.city}, ${team.state})` : ""}`}
                description={`${programName} is a ${team.sport || ""} travel/club program${ageLabel ? ` fielding ${ageLabel} teams` : ""}${team.city && team.state ? ` based in ${team.city}, ${team.state}` : ""}. View rosters, age groups, circuits and contact details on TravelSports.info.`}
                canonical={`/teams/${canonicalSlug}`}
                image={(team.photos && team.photos[0]) || undefined}
                jsonLd={{ "@context": "https://schema.org", "@type": "SportsTeam", name: programName, sport: team.sport || undefined, ...(team.city && team.state ? { location: { "@type": "Place", address: { "@type": "PostalAddress", addressLocality: team.city, addressRegion: team.state, addressCountry: "US" } } } : {}) }}
            />
            <ConfirmDialog
                open={showRelinquish}
                onOpenChange={setShowRelinquish}
                title="Relinquish ownership"
                description="Relinquish ownership of this listing? It will become unclaimed and anyone can claim it again."
                confirmLabel="Relinquish"
                destructive
                onConfirm={onRelinquish}
                testid="relinquish-confirm-dialog"
            />
            {editingProgram && (
                <div className="fixed inset-0 z-[100] bg-black/70 overflow-y-auto p-4 md:p-8" data-testid="edit-program-modal" onMouseDown={() => setEditingProgram(false)}>
                    <div className="max-w-3xl mx-auto" onMouseDown={(e) => e.stopPropagation()}>
                        <InlineAddTeamForm
                            sport={team.sport}
                            existing={{ base_name: programName, rosters }}
                            onCancel={() => setEditingProgram(false)}
                            onCreated={() => { setEditingProgram(false); setReloadKey((k) => k + 1); }}
                        />
                    </div>
                </div>
            )}
            {ownerEditOpen && (
                <div className="fixed inset-0 z-[100] bg-black/70 overflow-y-auto p-4 md:p-8" data-testid="owner-edit-modal" onMouseDown={() => setOwnerEditOpen(false)}>
                    <div className="max-w-4xl mx-auto bg-[#0a0a0a] border border-[#2a2a2a] p-4 sm:p-6" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-display text-2xl md:text-3xl tracking-wide">Edit — {programName}</h2>
                            <button onClick={() => setOwnerEditOpen(false)} data-testid="owner-edit-close-btn" className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-[#ff4500] transition-colors">Close</button>
                        </div>
                        <AddTeamForm
                            sport={team.sport}
                            existing={{ base_name: programName, rosters }}
                            onDone={() => window.location.reload()}
                        />
                        <div className="mt-10 border-t border-[#2a2a2a] pt-8" data-testid="edit-moderators-section">
                            <CollaboratorsManager entityType="team" entityId={team.id} accent="#ff4500" canManage />
                        </div>
                    </div>
                </div>
            )}
            {showMods && <ManageModeratorsModal entityType="team" entityId={team.id} name={programName} accent="#ff4500" onClose={() => setShowMods(false)} />}
            <div className="mt-6">
                <div>
                    {team.photos && team.photos.length > 0 ? (
                        <div data-testid="team-detail-photo-wrap">
                            <HeroCarousel
                                photos={team.photos}
                                name={team.name}
                                onOpen={openPhoto}
                                testid="team-hero-carousel"
                                badge={(team.logo || team.avatar) ? (
                                    <HeroLogoBadge src={team.logo || team.avatar} alt={`${team.name} logo`} bg={team.logo_bg} testid="team-detail-logo-badge" />
                                ) : null}
                            />
                        </div>
                    ) : team.photo ? (
                        <div className="relative aspect-[16/9] overflow-hidden bg-[#0a0a0a]" data-testid="team-detail-photo-wrap">
                            <img
                                src={team.photo}
                                alt={team.name}
                                data-testid="team-detail-photo"
                                className="block w-full h-full object-cover"
                            />
                            {(team.logo || team.avatar) && (
                                <HeroLogoBadge src={team.logo || team.avatar} alt={`${team.name} logo`} bg={team.logo_bg} testid="team-detail-logo-badge" />
                            )}
                        </div>
                    ) : team.logo ? (
                        <div
                            className="relative aspect-[16/9] overflow-hidden flex items-center justify-center bg-gradient-to-br from-[#1a1a1a] via-[#141414] to-[#0a0a0a] p-6"
                            data-testid="team-detail-logo-main"
                        >
                            <img
                                src={team.logo}
                                alt={team.name}
                                className="max-w-full max-h-full object-contain"
                            />
                        </div>
                    ) : (
                        <div className="relative aspect-[16/9] border border-[#2a2a2a] overflow-hidden bg-gradient-to-br from-[#1a1a1a] via-[#141414] to-[#0a0a0a] flex items-center justify-center">
                            <div
                                className="absolute inset-0 opacity-[0.05]"
                                style={{
                                    backgroundImage:
                                        "radial-gradient(circle at 50% 130%, #ff4500 0%, transparent 60%), repeating-linear-gradient(45deg, #ff4500 0 1px, transparent 1px 28px)",
                                }}
                            />
                            {team.logo && (
                                <TeamLogo
                                    team={team}
                                    className="relative h-44 w-44 md:h-56 md:w-56 bg-white p-5 border border-white/10 shadow-2xl"
                                    imgClassName="w-full h-full object-contain"
                                />
                            )}
                        </div>
                    )}

                    <div className="mt-6 flex flex-col md:flex-row md:items-start gap-6">
                        <div className="flex-1 min-w-0 flex items-start gap-4">
                            {!team.photo && (team.avatar || team.logo) && (
                                <img
                                    src={team.avatar || team.logo}
                                    alt={team.name}
                                    data-testid="team-detail-avatar"
                                    className={`hidden sm:block h-20 w-20 bg-white p-1 border border-[#2a2a2a] shrink-0 ${isAebl ? "rounded-none object-contain" : "rounded-full object-cover"}`}
                                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="sm:min-h-[5.5rem]">
                                {isClaimed && !isVerified && (
                                    <div className="text-[#ff4500] text-xs uppercase tracking-[0.3em] flex items-center gap-2">
                                        <span data-testid="pending-badge" className="inline-flex items-center gap-1 bg-[#2a2a2a] text-[#a0a0a0] text-[10px] uppercase tracking-[0.2em] px-2 py-0.5">
                                            Claim Pending
                                        </span>
                                    </div>
                                )}
                                <div className="flex items-center gap-2 min-w-0">
                                    <h1 ref={nameFitRef} className="font-display tracking-wide leading-none mt-1 whitespace-nowrap overflow-hidden min-w-0" data-testid="team-name">{programName}</h1>
                                    {isVerified && <BadgeCheck className="h-6 w-6 text-[#22c55e] shrink-0" data-testid="team-verified-check" />}
                                </div>
                                </div>
                                {!isAebl && !team.owner_verified && team.created_by && (
                                    <div className="mt-3"><CreatorTag userId={team.created_by} label="Added by" /></div>
                                )}
                                <div className={showSideAvatar ? "sm:-ml-24 mt-3" : "mt-3"}>
                                <div className="mt-2 flex items-center gap-3 flex-wrap">
                                    <EditHistory entity={team} entityType="team" entityId={team.id} onReverted={(fresh) => setTeam(fresh)} />
                                    <WikiEditModal type="team" entity={team} onSaved={() => window.location.reload()} />
                                    <FlagButton targetType="team" targetId={team.id} label="Report listing" />
                                </div>
                                {isAebl ? (
                                    <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2">
                                        <MapLink
                                            direct
                                            parts={[team.street_address, team.city, team.state, team.zip]}
                                            testid="team-location"
                                            className="text-[#a0a0a0] inline-flex items-center gap-1.5 text-sm hover:text-[#ff4500] transition-colors"
                                        >
                                            <MapPin className="h-4 w-4" /> {team.street_address ? `${team.street_address}, ` : ""}{team.city}, {team.state} {team.zip}
                                        </MapLink>
                                        <DistanceZip distanceMiles={team.distance_miles} zip={zipState} onZip={handleZip} accent="#ff4500" testid="team-distance" />
                                    </div>
                                ) : (
                                <>
                                <MapLink
                                    direct
                                    parts={[team.street_address, team.city, team.state, team.zip]}
                                    testid="team-location"
                                    className="mt-2 text-[#a0a0a0] inline-flex items-center gap-1.5 text-sm hover:text-[#ff4500] transition-colors"
                                >
                                    <MapPin className="h-4 w-4" /> {team.street_address ? `${team.street_address}, ` : ""}{team.city}, {team.state} {team.zip}
                                </MapLink>
                                <div className="mt-2">
                                    <DistanceZip distanceMiles={team.distance_miles} zip={zipState} onZip={handleZip} accent="#ff4500" testid="team-distance" />
                                </div>
                                </>
                                )}
                                {(() => {
                                    const rlist = rosters.length ? rosters : [team];
                                    const genders = [...new Set(rlist.map((r) => r.gender).filter(Boolean))];
                                    const gl = (genders.length ? genders : (team.gender ? [team.gender] : [])).map((x) => (x === "F" ? "Girls" : "Boys"));
                                    const parts = programAudienceParts(rlist);
                                    return (gl.length || parts.length) ? (
                                        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="team-tags">
                                            {gl.map((t) => (
                                                <span key={`g-${t}`} data-testid={`team-gender-${t.toLowerCase()}`} className="inline-flex items-center text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-[#2a2a2a] bg-[#141414] text-[#cfcfcf]">{t}</span>
                                            ))}
                                            {parts.map((p, i) => (
                                                <span key={`p-${i}`} data-testid={`team-audience-${i}`} className={`items-center text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-[#141414] ${p.provided ? "inline-flex border-2 border-white text-white font-bold" : "hidden md:inline-flex border border-[#2a2a2a] text-[#cfcfcf]"}`}>{p.text}</span>
                                            ))}
                                        </div>
                                    ) : null;
                                })()}
                                {team.circuit && (
                                    <Link to={`/${sportToSlug(team.sport)}/${circuitSlug(team.circuit)}`} data-testid="team-circuit-tag" className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-[#ff4500]/50 bg-[#ff4500]/10 text-[#ff4500] hover:bg-[#ff4500]/20 transition-colors">
                                        {team.circuit}<CircuitFlag sport={team.sport} name={team.circuit} />
                                    </Link>
                                )}
                                </div>
                            </div>
                        </div>
                        <div className="w-full md:w-72 shrink-0 flex flex-col gap-3 items-stretch" data-testid="team-actions">
                            {/* 1 + 2: Share and Follow compressed onto the same row */}
                            <div className="flex items-stretch gap-3">
                            <ShareButton
                                accent="#ff4500"
                                outline
                                className={`${isFollowingTeam ? "flex-[0.55]" : "flex-1"} h-12 text-xs`}
                                iconClass="h-4 w-4"
                                testid="share-team-btn"
                                shareUrl={`${process.env.REACT_APP_BACKEND_URL}/api/share/team/${team.id}`}
                                title={`${team.name} · ${team.sport}`}
                                description={`${team.age_group} · ${team.city}, ${team.state}`}
                            />
                            {/* Follow */}
                            <div className={`${isFollowingTeam ? "flex-[1.45]" : "flex-1"} flex items-stretch min-w-0`}>
                                <Button
                                    data-testid="follow-btn"
                                    title={isFollowingTeam ? "Following" : "Follow"}
                                    aria-label={isFollowingTeam ? "Following" : "Follow"}
                                    onClick={onFollow}
                                    disabled={followBusy}
                                    className="flex-1 min-w-0 overflow-hidden rounded-none uppercase tracking-wider whitespace-nowrap h-12 text-xs bg-[#141414] border border-[#ff4500] text-[#ff4500] hover:bg-[#ff4500] hover:text-white"
                                >
                                    {isFollowingTeam
                                        ? <BookmarkCheck className="h-4 w-4 mr-2" />
                                        : <BookmarkPlus className="h-4 w-4 mr-2" />}
                                    {isFollowingTeam ? "Following" : "Follow"}
                                </Button>
                                {isFollowingTeam && (
                                    <FollowAlertsBell targetType="team" targetId={team.id} size={16} triggerStyle={{ color: "#ff4500" }} triggerClassName="inline-flex items-center justify-center h-12 w-12 shrink-0 bg-[#141414] border border-l-0 border-[#ff4500] hover:bg-[#ff4500]/10 transition-colors" />
                                )}
                            </div>
                            </div>
                            {/* 3. Claimed by / Added by card */}
                            {isClaimed ? (
                                <ClaimedByTag userId={team.owner_user_id} contextType="team" contextId={team.id} reportType="team" verified={isVerified} />
                            ) : (team.created_by && (
                                <CreatorTag block userId={team.created_by} label="Posted by" />
                            ))}
                            {/* Owner / admin actions */}
                            {isOwner && (
                                <Button
                                    data-testid="public-edits-btn"
                                    onClick={onTogglePublicEdit}
                                    className={`w-full rounded-none uppercase tracking-wider text-xs bg-[#141414] h-10 border ${pubEdit ? "border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e] hover:text-black" : "border-red-500 text-red-500 hover:bg-red-500 hover:text-white"}`}
                                >
                                    <Globe className="h-4 w-4 mr-2" /> Public Edits {pubEdit ? "ON" : "OFF"}
                                </Button>
                            )}
                            {isOwner && (
                                <Button
                                    data-testid="add-moderators-btn"
                                    onClick={() => setShowMods(true)}
                                    className="w-full rounded-none uppercase tracking-wider text-xs bg-[#141414] border border-[#ff4500] text-[#ff4500] hover:bg-[#ff4500] hover:text-white h-10"
                                >
                                    <Users className="h-4 w-4 mr-2" /> Add moderators
                                </Button>
                            )}
                            {isOwner ? (
                                <Button
                                    data-testid="relinquish-btn"
                                    onClick={() => setShowRelinquish(true)}
                                    className="w-full rounded-none uppercase tracking-wider text-xs bg-[#141414] border border-red-500 text-red-500 hover:bg-red-500 hover:text-white h-10"
                                >
                                    Relinquish Ownership
                                </Button>
                            ) : (user && !isClaimed && (
                                <Button
                                    data-testid="claim-team-btn"
                                    onClick={onClaim}
                                    className="w-full rounded-none uppercase tracking-wider text-xs bg-[#141414] border border-[#ff4500] text-[#ff4500] hover:bg-[#ff4500] hover:text-white h-10"
                                >
                                    <BadgeCheck className="h-4 w-4 mr-2" /> Claim Ownership
                                </Button>
                            ))}
                            {canDelete && (
                                <div>
                                    <Button
                                        data-testid="delete-team-btn"
                                        onClick={onDelete}
                                        className="w-full rounded-none uppercase tracking-wider text-xs bg-[#141414] border border-red-500 text-red-500 hover:bg-red-500 hover:text-white h-10"
                                    >
                                        <Trash2 className="h-4 w-4 mr-2" /> Delete Listing
                                    </Button>
                                    {canCreatorDelete && !user?.is_admin && deleteRemaining && (
                                        <div className="text-[10px] text-red-400 text-center mt-1 tracking-wider" data-testid="delete-timer">You can delete for {deleteRemaining}</div>
                                    )}
                                    {user?.is_admin && (
                                        <div className="text-[10px] text-[#a0a0a0] text-center mt-1 tracking-wider">Admin delete</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="mt-8">
                        <h2 className="font-display text-2xl tracking-wide mb-3">About the Program</h2>
                        <p className="text-[#cfcfcf] leading-relaxed">{team.description}</p>
                    </div>

                    {photoLightbox}

                    <div className="mt-8" data-testid="team-contact">
                        <div className="flex flex-wrap items-baseline gap-x-3 mb-3">
                            <h2 className="font-display text-2xl tracking-wide">Contact information</h2>
                            <span className="text-[#cfcfcf] leading-relaxed">(Only post publicly available contact information)</span>
                        </div>
                        {team.coach ? (
                            <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="team-director-line">
                                <span className="text-xs uppercase tracking-[0.2em] font-bold text-white pb-1.5 border-b-[3px] border-[#ff4500]">{team.coach_role || "Director"}</span>
                                <span className="text-lg font-semibold text-white">{team.coach}</span>
                                {(team.contacts || []).length > 0 && (
                                    <ContactsDisplay contacts={team.contacts} accent="#ff4500" hideTitle testidPrefix="team-contact-disp" />
                                )}
                            </div>
                        ) : (team.contacts || []).length > 0 && (
                            <div className="mb-3"><ContactsDisplay contacts={team.contacts} accent="#ff4500" hideTitle testidPrefix="team-contact-disp" /></div>
                        )}
                        {(() => {
                            const rows = sortedProgramRosters
                                .map((r) => ({ r, hc: rosterHeadCoach(r) }))
                                .filter(({ hc }) => hc.contacts.length > 0);
                            if (!rows.length) return null;
                            return (
                                <div className="mb-4 space-y-3" data-testid="team-coaches-list">
                                    {rows.map(({ r, hc }, i) => (
                                        <div key={r.id} className="border border-[#2a2a2a] p-3 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid={`team-coach-${i}`}>
                                            <span className="text-xs uppercase tracking-[0.2em] font-bold text-white pb-1.5 border-b-[3px] border-[#ff4500]" data-testid={`team-coach-${i}-pill`}>{rosterLabel(r)}</span>
                                            <span className="text-base font-semibold text-white">{hc.name || "—"}</span>
                                            {hc.contacts.length > 0 && (
                                                <ContactsDisplay contacts={hc.contacts} accent="#ff4500" hideTitle testidPrefix={`team-coach-${i}-contact`} />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            );
                        })()}
                        <div className="flex flex-wrap items-start gap-3">
                            {team.contact_email && (
                                <a href={`mailto:${team.contact_email}`} data-testid="contact-email" className="inline-flex items-center gap-3 border border-[#2a2a2a] p-4 hover:border-[#ff4500] transition-colors w-fit max-w-full">
                                    <Mail className="h-4 w-4 text-[#ff4500] shrink-0" />
                                    <div className="min-w-0">
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Email</div>
                                        <div className="text-sm break-all">{team.contact_email}</div>
                                    </div>
                                </a>
                            )}
                            {team.contact_phone && (
                                <a href={`tel:${team.contact_phone}`} data-testid="contact-phone" className="inline-flex items-center gap-3 border border-[#2a2a2a] p-4 hover:border-[#ff4500] transition-colors w-fit max-w-full">
                                    <Phone className="h-4 w-4 text-[#ff4500] shrink-0" />
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Phone</div>
                                        <div className="text-sm">{team.contact_phone}</div>
                                    </div>
                                </a>
                            )}
                            {team.website && (
                                <a href={team.website} target="_blank" rel="noopener noreferrer" data-testid="contact-website" className="inline-flex items-center gap-3 border border-[#2a2a2a] p-4 hover:border-[#ff4500] transition-colors w-fit max-w-full">
                                    <Globe className="h-4 w-4 text-[#ff4500] shrink-0" />
                                    <div className="min-w-0">
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Website</div>
                                        <div className="text-sm break-all">{team.website}</div>
                                    </div>
                                    <ExternalLink className="h-3.5 w-3.5 text-[#a0a0a0] shrink-0" />
                                </a>
                            )}
                            {team.socials && (
                                <div className="border border-[#2a2a2a] p-4 w-fit max-w-full">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">Social profiles</div>
                                    <SocialsDisplay socials={team.socials} testIdPrefix="team-socials-display" />
                                </div>
                            )}
                        </div>
                    </div>

                    <EntityEvents params={{ entity_owner: team.owner_user_id || undefined, entity_kind: "team", team: team.id, owner_program_name: programName }} manageTeamId={(isOwner || user?.is_admin) ? team.id : ""} greenHeading refreshToken={eventsBump} addEventLink={isOwner && isVerified ? `/add/${encodeURIComponent(team.sport || "Basketball")}?type=event&claim_type=team&claim_id=${team.id}` : ""} />

                    <EntityEvents
                        params={{ mention: programName, exclude_owner: team.owner_user_id || team.created_by, exclude_kind: "team" }}
                        title={`Events mentioning ${programName}`}
                        greenHeading
                        manageTeamId={(isOwner || user?.is_admin) ? team.id : ""}
                        canClaim={!!(user && (user.is_admin || team.owner_user_id === user.id))}
                        refreshToken={eventsBump}
                        onChanged={() => setEventsBump((x) => x + 1)}
                    />

                    <MessageBoard entityType="team" entityId={team.id} accent="#ff4500" />

                    <Reviews teamId={team.id} canRespond={isManaging} />
                </div>
            </div>

            <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
                <DialogContent className="bg-[#141414] border border-[#2a2a2a] rounded-none text-white" data-testid="claim-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-3xl tracking-wide">Claim {team.name}</DialogTitle>
                        <DialogDescription className="text-[#a0a0a0]">
                            Submit a short verification message — admin will review. Once verified, you'll get a "Verified Owner" badge on your team's listing.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 pt-2">
                        <Textarea
                            data-testid="claim-message"
                            value={claimMsg}
                            onChange={(e) => setClaimMsg(e.target.value)}
                            placeholder="I'm the head coach / director of this program. Verify via team website, social handle, or league registration…"
                            rows={4}
                            className="rounded-none bg-[#0a0a0a] border-[#2a2a2a] focus-visible:ring-0"
                        />
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">
                            {claimMsg.length} / 1000 characters · minimum 10
                        </div>
                        <div className="pt-2 border-t border-[#2a2a2a]">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">Proof (optional) — speeds up approval if your email doesn't match</div>
                            <Input
                                data-testid="claim-proof-link"
                                value={claimLink}
                                onChange={(e) => setClaimLink(e.target.value)}
                                placeholder="Link to proof (team site, roster, social profile…)"
                                className="rounded-none bg-[#0a0a0a] border-[#2a2a2a] h-10 mb-2"
                            />
                            <PhotoUpload value={claimImage} onChange={setClaimImage} testid="claim-proof-image" label="Upload a screenshot" dragPaste />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            data-testid="claim-cancel"
                            variant="ghost"
                            onClick={() => setClaimOpen(false)}
                            className="rounded-none border border-[#2a2a2a] hover:bg-transparent hover:border-white/30"
                        >
                            Cancel
                        </Button>
                        <Button
                            data-testid="claim-submit"
                            onClick={onClaim}
                            disabled={claiming || claimMsg.trim().length < 10}
                            className="rounded-none bg-[#ff4500] hover:bg-[#ff5722] uppercase tracking-[0.2em] text-xs font-semibold"
                        >
                            {claiming ? "Submitting…" : "Submit Claim"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Meta({ icon, label, value }) {
    return (
        <div className="p-4 border-r border-[#2a2a2a] last:border-r-0">
            <div className="flex items-center gap-1.5 text-[#ff4500]">{icon}</div>
            <div className="font-display text-2xl tracking-wide mt-1">{value}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] mt-0.5">{label}</div>
        </div>
    );
}

function RosterField({ label, children }) {
    return (
        <div className="min-w-0 md:text-left">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">{label}</div>
            <div className="text-sm mt-0.5">{children}</div>
        </div>
    );
}